import { SUBSCRIPTIONS_ENABLED } from "./billing.constants";
// ─────────────────────────────────────────────────────────────────────────────
// JEDEN PLIK DO EDYCJI — zmień tutaj ceny/plany/feature gates
// Logika gating nie wymaga zmian przy aktualizacji tego pliku
// ─────────────────────────────────────────────────────────────────────────────

export type PlanTier = "free" | "agent" | "biuro" | "agencja";

// Hierarchia — każdy wyższy plan zawiera uprawnienia niższego
export const PLAN_HIERARCHY: PlanTier[] = ["free", "agent", "biuro", "agencja"];

// ─── Feature gates ────────────────────────────────────────────────────────────
// Klucz = nazwa ficzera, wartość = minimalny plan wymagany
// Dodaj nowy ficzer tutaj — gating działa automatycznie

export const FEATURE_GATES = {
  watermark:        "agent",    // Znak wodny z własnym logo na zdjęciach
  brand:            "biuro",    // Panel marki (logo, kolory, stopka)
  // UWAGA: tworzenie agencji NIE MOŻE być bramkowane planem — checkout planu Biuro
  // wymaga istniejącej agencji (AGENCY_REQUIRED), więc bramka `agency_create`
  // zamykała pętlę: nie kupisz planu bez biura, nie założysz biura bez planu.
  // Usunięta 2026-08-24; backend POST /v1/agency też jest celowo bez bramki.
  agency_invite:    "biuro",    // Zapraszanie członków do agencji
  priority_gen:     "biuro",    // Priorytetowe generowanie AI
  premium_quality:  "biuro",    // Generowanie obrazów modelem pro (jakość premium)
  member_limits:    "agencja",  // Limity kredytów per agent
  api_access:       "agencja",  // Publiczne API (przyszłość)
} as const satisfies Record<string, PlanTier>;

export type FeatureKey = keyof typeof FEATURE_GATES;

// ─── canUse ───────────────────────────────────────────────────────────────────
// Główna funkcja gating — używana zarówno w frontend jak i backend

export function canUse(feature: FeatureKey, tier: PlanTier): boolean {
  const required = PLAN_HIERARCHY.indexOf(FEATURE_GATES[feature]);
  const current  = PLAN_HIERARCHY.indexOf(tier);
  return current >= required;
}

// ─── Limity ofert per plan ────────────────────────────────────────────────────
// Oferta jest trwała (zdjęcia w niej nie wygasają — patrz retention.constants.ts),
// więc liczba ofert jest tym, co realnie ogranicza plan darmowy.
//
// UWAGA: modelu subskrypcji jeszcze NIE MA — dziś każdy user jest "free"
// (`resolveSubscriptionTier` zwraca "free", dopóki nie ma wiersza w
// `user_subscriptions`). Wartości dla planów płatnych są ZAŁOŻENIEM przyjętym
// 2026-08-07, żeby gating był gotowy przed wdrożeniem subskrypcji: kod czyta
// limit z tej mapy, więc uruchomienie planów = uzupełnienie liczb, nie zmiana logiki.
// null = bez limitu.

/**
 * Limit ofert planu darmowego. Używany WYŁĄCZNIE jako zapasowa wartość w UI, gdy
 * API nie zwróciło jeszcze `offerLimit` (okno przejściowe deployu). Prawdziwym
 * źródłem jest `subscription_plans.max_active_offers` czytane przez uprawnienia —
 * front nie liczy limitu sam, bo planu użytkownika nie zna.
 *
 * Bezpieczniej pokazać limit, którego chwilowo nie ma, niż obiecać brak limitu
 * i odbić użytkownika dopiero przy zapisie.
 */
export const FREE_PLAN_OFFER_LIMIT = 3;

/**
 * Komunikat przy odbiciu od limitu ofert. Bierze LICZBĘ, nie plan: limit przychodzi
 * z uprawnień, więc druga mapa progów po stronie frontu rozjechałaby się z tą, o którą
 * faktycznie odbija zapis. Wspólny dla API i UI, żeby zdanie było jedno.
 */
export function offerLimitMessage(limit: number | null): string {
  if (limit === null) return "";
  // Przy zawieszonej sprzedaży abonamentów (SUBSCRIPTIONS_ENABLED) nie odsyłamy
  // do planu, którego nie da się kupić.
  return SUBSCRIPTIONS_ENABLED
    ? `Twój plan pozwala mieć ${limit} ofert. Usuń niepotrzebną ofertę albo przejdź na plan wyższy, żeby dodać kolejną.`
    : `Twój plan pozwala mieć ${limit} ofert. Usuń niepotrzebną ofertę, żeby dodać kolejną.`;
}

// ─── Plan definitions (UI + seeding) ─────────────────────────────────────────
// Placeholder ceny — zaktualizuj gdy podjęta decyzja biznesowa
// priceM / priceY w groszach (żeby uniknąć float przy Stripe)

export interface PlanDef {
  id: PlanTier;
  name: string;
  tagline: string;
  priceM: number;        // miesięcznie PLN (placeholder)
  priceY: number;        // rocznie PLN (placeholder)
  creditsM: number;      // kredyty w planie miesięcznym
  seats: string;
  accent: string;
  rec?: boolean;
  stripePriceIdM?: string;  // uzupełnij po stworzeniu produktów w Stripe
  stripePriceIdY?: string;
}

export const PLAN_DEFS: PlanDef[] = [
  {
    id: "agent",
    name: "Agent",
    tagline: "Dla pojedynczego pośrednika",
    priceM: 49,   // TODO: ustal z decyzją biznesową
    priceY: 39,
    creditsM: 40,
    seats: "1 użytkownik",
    accent: "#22d3ee",
  },
  {
    id: "biuro",
    name: "Biuro",
    tagline: "Dla biura nieruchomości",
    priceM: 149,  // TODO: ustal z decyzją biznesową
    priceY: 119,
    creditsM: 150,
    seats: "do 5 użytkowników",
    accent: "#8b5cf6",
    rec: true,
  },
  {
    id: "agencja",
    name: "Agencja",
    tagline: "Dla sieci i dużych zespołów",
    priceM: 389,  // TODO: ustal z decyzją biznesową
    priceY: 309,
    creditsM: 500,
    seats: "bez limitu",
    accent: "#f59e0b",
  },
];

export const PLAN_BY_ID: Record<PlanTier, PlanDef | undefined> = {
  free:    undefined,
  agent:   PLAN_DEFS[0],
  biuro:   PLAN_DEFS[1],
  agencja: PLAN_DEFS[2],
};

/**
 * Rodzina planu z `subscription_plans.family` → tier z PLAN_HIERARCHY.
 *
 * Katalog planów w bazie mówi „agent_start", „office_pro"; gating w kodzie mówi
 * „agent", „biuro". Bez tego mapowania każdy plan z bazy degradował do `free`,
 * bo `plan_id` nigdy nie należy do PLAN_HIERARCHY.
 */
export const PLAN_FAMILY_TIERS: Record<string, PlanTier> = {
  agent:  "agent",
  office: "biuro",
  agency: "agencja",
};

/**
 * Plany z v1, które zostały w `subscription_plans` bez rodziny z migracji 026
 * (dostały DEFAULT 'agent'). Mapujemy je po id, żeby konto zastane nie straciło
 * uprawnień w dniu, w którym zaczęliśmy czytać plan z subskrypcji.
 */
export const LEGACY_PLAN_TIERS: Record<string, PlanTier> = {
  free:       "free",
  basic:      "agent",
  pro:        "agent",
  agency_pro: "agencja",
};

/**
 * Plan z katalogu → tier. JEDYNE miejsce, które tłumaczy te dwa słowniki.
 *
 * Kolejność reguł: plan zastany z v1 (po id) → poziom `free` w każdej rodzinie →
 * rodzina. Nieznana rodzina schodzi do `free` — brak uprawnień jest bezpieczniejszy
 * niż wpuszczenie kogoś na plan, którego nie rozumiemy.
 */
export function planToTier(plan: { id: string; family?: string; level?: string }): PlanTier {
  const legacy = LEGACY_PLAN_TIERS[plan.id];
  if (legacy) return legacy;
  if (plan.level === "free") return "free";
  return PLAN_FAMILY_TIERS[plan.family ?? ""] ?? "free";
}

export function getPlanLabel(tier: PlanTier): string {
  const map: Record<PlanTier, string> = {
    free:    "Free",
    agent:   "Agent",
    biuro:   "Biuro",
    agencja: "Agencja",
  };
  return map[tier];
}
